<template>
  <div class="gs-app gm-sender-settings">
    <div class="w-full">
      <!-- Loader -->
      <portal to="loaderTarget">
        <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
          <gd-loader :label="$t('frontend_loading')"/>
        </div>
      </portal>

      <!-- go back button -->
      <portal to="headerBreadCrumb">
        <div class="cursor-pointer self-center text-grey-500 hover:text-primary" @click="moveBack">
          <ExitIcon class="h-5 w-5"/>
        </div>
      </portal>


      <div id="user-view" class="gm-sender-settings__card flex flex-col bg-white rounded w-full pt-4">
        <div class="gm-sender-settings__header px-6 pt-1 pb-4 create-sender-header">
          <div class="text-xl text-lg font-bold">
            {{ $t('frontend_groovemail_segment_subscribe_settings') }}
          </div>
          <div class="text-grey-800 text-sm">
            {{ $t('frontend_groovemail_segment_subscribe_configure') }}
          </div>
        </div>
        <div class="flex w-full text-left">
          <div class="w-full border-b border-grey-200">
            <gd-tabs
              :tabs="tabs"
              :currentTab="currentTab"
              :disableNotCompleted="false"
              :wrapper-class="'px-6 gs-default-tabs'"
              :tab-class="mainTabClass"
              :tab-active-class="'border-primary border-b-2 text-primary gs-default-tabs-item-active'"
              :line-class="'gs-default-tabs-active-line'"
              @onClick="handleTabChange"
            />
          </div>
        </div>
        <div class="gm-sender-settings__body gs-refresh-controls relative p-6 pb-0">
          <div>
            <div v-if="currentTab === 'senderInfo'">
              <div class="flex flex-col mb-6">
                <label class="font-semibold text-sm text-grey-700 mb-2">
                  {{
                    $t(
                      'frontend_groovemail_segment_subscribe_sender_identity'
                    )
                  }}
                </label>
                <text-input
                  variant="base"
                  v-model="model.name"
                  :placeholder="$t('frontend_groovemail_sender_enter_sender_identity')"
                  :validator="$v.model.name"
                />
              </div>

              <div class="flex flex-col mb-6">
                <label class="font-semibold text-sm text-grey-700 mb-2">
                  {{
                    $t('frontend_groovemail_segment_subscribe_sender_name')
                  }}
                </label>
                <text-input
                  variant="base"
                  v-model="model.sender_name"
                  :placeholder="$t('frontend_groovemail_sender_enter_sender_name')"
                  :validator="$v.model.sender_name"
                />
              </div>

              <div class="flex flex-col mb-6">
                <label class="font-semibold text-sm text-grey-700 mb-2">
                  {{
                    $t('frontend_groovemail_segment_subscribe_sender_email')
                  }}
                </label>
                <text-input
                  variant="base"
                  v-model="model.sender_email"
                  :placeholder="$t('frontend_groovemail_sender_enter_sender_email')"
                  :validator="$v.model.sender_email"
                />
              </div>

              <div class="flex flex-col mb-6">
                <label class="font-semibold text-sm text-grey-700 mb-2">
                  {{
                    $t('frontend_groovemail_segment_subscribe_reply_email')
                  }}
                </label>
                <text-input
                  variant="base"
                  v-model="model.reply_email"
                  :placeholder="$t('frontend_groovemail_sender_enter_reply_email')"
                  :validator="$v.model.reply_email"
                />
              </div>

              <div class="flex flex-col mb-6">
                <label class="font-semibold text-sm text-grey-700 mb-2">
                  {{ $t('frontend_groovemail_segment_subscribe_default') }}
                </label>
                <gd-toggle
                  bgClass="bg-grey-300"
                  :value="model.is_default"
                  @change="changeDefault"
                />
              </div>

              <div class="flex flex-col mb-6">
                <label class="font-semibold text-sm text-grey-700 mb-2">
                  <span
                    >{{ $t('frontend_groovemail_email_sender_smtp_driver') }}
                  </span>
                </label>

                <div
                  class="custom-radio-list"
                  v-for="(option, index) in smtp_options"
                  :key="index"
                >
                  <gd-custom-radio
                    v-model="model.smtp_driver"
                    :option="option"
                    value="model.smtp_driver"
                    @onUpdateOption="smtpDriverUpdated(option)"
                  ></gd-custom-radio>
                </div>
                <div
                  class="flex text-sm text-grey-700 items-center mb-6"
                  v-if="model.smtp_driver === 3"
                >
                  <InfoIcon class="mr-2 text-grey-600 h-4 w-4" />
                  {{
                    $t(
                      'frontend_groovemail_need_help_for_connecting_sendgrid_account'
                    )
                  }}<span class="mx-1">-</span>
                  <span
                    class="cursor-pointer text-primary underline"
                    @click="
                      openWalkthroughVideoForExternalSmtp(model.smtp_driver)
                    "
                  >
                    {{
                      $t(
                        'frontend_groovemail_click_here_for_sendgrid_video_link'
                      )
                    }} </span
                  ><span>.</span>
                </div>
                <div
                  class="flex text-sm text-grey-700 items-center mb-6"
                  v-if="model.smtp_driver === 5"
                >
                  <InfoIcon class="mr-2 text-grey-600 h-4 w-4 min-w-4" />
                  {{
                    $t(
                      'frontend_groovemail_need_help_for_connecting_aws_account'
                    )
                  }}<span class="mx-1">-</span>
                  <span
                    class="cursor-pointer text-primary underline"
                    @click="openStepsForAwsSmtp"
                  >
                    {{
                      $t('frontend_groovemail_click_here_for_aws_steps')
                    }} </span
                  ><span
                    >.
                    {{ $t('frontend_groovemail_mandatory_aws_setup_note') }}
                  </span>
                </div>
                <div
                  class="flex text-sm text-grey-700 items-center mb-6"
                  v-if="model.smtp_driver === 2"
                >
                  <InfoIcon class="mr-2 text-grey-600 h-4 w-4"/>
                  {{
                    $t(
                      'frontend_groovemail_need_help_for_connecting_mailgun_account'
                    )
                  }}<span class="mx-1">-</span>
                  <span
                    class="cursor-pointer text-primary underline"
                    @click="openStepsForMailgunSmtp"
                  >
                    {{
                      $t('frontend_groovemail_click_here_for_aws_steps')
                    }} </span
                  ><span>.</span>
                </div>
                <div v-if="model.smtp_driver === 6" class="text-sm text-grey-700 mb-6">
                  <p>Use a Full access Resend API key and a sender address on a domain verified in Resend. Saving sends a verification message to your account email.</p>
                  <p class="mt-2">GrooveMail automatically connects delivery notifications and synchronizes suppressed recipients. Bounced, complained, and suppressed addresses are blocked from future Resend sends in your GrooveMail account.</p>
                  <a href="https://resend.com/docs/dashboard/domains/introduction" target="_blank" rel="noopener noreferrer" class="text-primary underline">Resend setup guide</a>
                </div>
                <div>
                  <p
                    v-if="this.formSubmitted && this.model.smtp_driver === 0"
                    class="w-full error text-danger mt-1 text-sm"
                  >
                    {{
                      $t(
                        'frontend_groovemail_email_sender_please_select_smtp_driver'
                      )
                    }}
                  </p>
                </div>
              </div>

              <div
                class="flex flex-col mb-6"
                v-if="model.smtp_driver === 2 || model.smtp_driver === 5"
              >
                <label class="font-semibold text-sm text-grey-700 mb-2">
                  {{
                    $t(
                      'frontend_groovemail_email_sender_settings_user_name_key'
                    )
                  }}
                </label>
                <text-input
                  variant="base"
                  v-model="smtp_model.user_name"
                  :placeholder="$t('frontend_groovemail_email_sender_settings_enter_user_name')"
                  :validator="$v.smtp_model.user_name"
                />
              </div>

              <div
                class="flex flex-col mb-6"
                v-if="
                  model.smtp_driver === 2 ||
                  model.smtp_driver === 3 ||
                  model.smtp_driver === 4 ||
                  model.smtp_driver === 5 ||
                  model.smtp_driver === 6
                "
              >
                <label class="font-semibold text-sm text-grey-700 mb-2">
                  {{
                    model.smtp_driver === 2 || model.smtp_driver === 5
                      ? $t(
                          'frontend_groovemail_email_sender_settings_password'
                        )
                      : $t(
                          'frontend_groovemail_email_sender_settings_api_key'
                        )
                  }}
                </label>
                <text-input
                  variant="base"
                  v-model="smtp_model.api_key"
                  :placeholder="
                    model.smtp_driver === 2 || model.smtp_driver === 5
                      ? $t(
                          'frontend_groovemail_email_sender_settings_enter_password'
                        )
                      : $t(
                          'frontend_groovemail_email_sender_settings_enter_api_key'
                        )
                  "
                  :validator="$v.smtp_model.api_key"
                />
              </div>

              <div class="flex flex-col mb-6" v-if="model.smtp_driver === 2">
                <label class="font-semibold text-sm text-grey-700 mb-2">
                  {{
                    $t(
                      'frontend_groovemail_email_sender_settings_webhook_signing_key'
                    )
                  }}
                </label>
                <text-input
                  variant="base"
                  v-model="smtp_model.webhook_signing_key"
                  :placeholder="$t('frontend_groovemail_email_sender_settings_enter_webhook_signing_key')"
                  :validator="$v.smtp_model.webhook_signing_key"
                />
              </div>

              <div class="flex flex-col mb-6" v-if="model.smtp_driver === 5">
                <label class="font-semibold text-sm text-grey-700 mb-2">
                  {{
                    $t(
                      'frontend_groovemail_email_sender_settings_server_name'
                    )
                  }}
                </label>
                <text-input
                  variant="base"
                  v-model="smtp_model.server"
                  :placeholder="$t('frontend_groovemail_email_sender_settings_enter_server_name')"
                  :validator="$v.smtp_model.server"
                />
              </div>
              <div class="flex flex-col mb-6" v-if="model.smtp_driver === 5">
                <label class="font-semibold text-sm text-grey-700 mb-2">
                  {{
                    $t(
                      'frontend_groovemail_email_sender_settings_configuration_set'
                    )
                  }}
                </label>
                <text-input
                  variant="base"
                  v-model="smtp_model.configuration_set"
                  :placeholder="$t('frontend_groovemail_email_sender_settings_enter_configuration_set')"
                  :validator="$v.smtp_model.configuration_set"
                />
              </div>
              <div
                class="mb-2"
                v-if="
                  this.model.smtp_driver === 2 ||
                  this.model.smtp_driver === 3 ||
                  this.model.smtp_driver === 4 ||
                  this.model.smtp_driver === 5
                "
              >
                <label class="font-semibold text-sm text-grey-700">
                  {{ $t('frontend_groovemail_senders_webhook_url') }}
                </label>
                <label class="font-semibold text-sm text-grey-700">
                  {{
                    $t('frontend_groovemail_senders_webhook_selected_events')
                  }}
                </label>
              </div>
              <div
                class="flex mb-6"
                v-if="
                  this.model.smtp_driver === 2 ||
                  this.model.smtp_driver === 3 ||
                  this.model.smtp_driver === 4 ||
                  this.model.smtp_driver === 5
                "
              >
                <div class="w-11/12">
                  <text-input
                    variant="base"
                    v-model="webhookUrl"
                    :placeholder="$t('frontend_product_step8_webhook')"
                    :disabled="true"
                  />
                </div>
                <gd-button
                  v-clipboard:copy="webhookUrl"
                  v-clipboard:success="onCopyWebhookUrl"
                  v-clipboard:error="onErrorWebhookUrl"
                  class="w-1/12 ml-2"
                  variant="primary"
                  size="auto"
                >
                  {{ $t('frontend_copy') }}
                </gd-button>
              </div>
            </div>
            <div v-if="currentTab === 'businessInfo'">
              <div class="flex flex-col mb-6">
                <label class="font-semibold text-sm text-grey-700 mb-2">{{
                  $t('frontend_groovemail_segment_company_name')
                }}</label>
                <text-input
                  variant="base"
                  v-model="model.company_name"
                  :placeholder="$t('frontend_groovemail_sender_enter_company_name')"
                  :validator="$v.model.company_name"
                />
              </div>

              <div class="flex flex-col mb-6">
                <label class="font-semibold text-sm text-grey-700 mb-2">{{
                  $t('frontend_groovemail_segment_company_address')
                }}</label>
                <text-input
                  variant="base"
                  v-model="model.address"
                  :placeholder="$t('frontend_groovemail_sender_enter_address')"
                  :validator="$v.model.address"
                />
              </div>
            </div>
          </div>
          <div class="flex justify-end pb-6 create-sender-save-btn">
            <gd-button
              variant="primary"
              :disabled="this.userCanMail ? false : true"
              @click="userCanMail ? createNewSender() : null"
            >
              {{ $t('frontend_save') }}
            </gd-button>
          </div>
        </div>
      </div>
    </div>

    <gs-named-modal
      id="external-smtp-setup-walkthrough-video"
      name="external-smtp-setup-walkthrough-video"
      :title="
        $t(
          'frontend_groovemail_need_help_for_connecting_external_smtp_account_title'
        )
      "
    >
      <div class="p-6">
	      <ContentInIframe
          :iframeSrc="turialLinkForExternalSmtp"
          iframeClasses="border border-grey-200"
          iframeWidth="550"
          iframeHeight="310"
          :iframeTitle="$t('frontend_groovemail_sendgrid_smtp_setup')"
          iframeAllowData="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        >
        </ContentInIframe>
      </div>
    </gs-named-modal>

    <gs-named-modal
      id="smtp-setup-walkthrough-steps"
      name="smtp-setup-walkthrough-steps"
      :title="
        $t('frontend_groovemail_need_help_for_connecting_aws_account_title')
      "
    >
      <SenderCreationSteps :steps="senderCreationSteps"></SenderCreationSteps>
    </gs-named-modal>

    <gs-named-modal
      id="senders-detail-modal"
      name="senders-detail-modal"
      :title="$t('frontend_groovemail_campains_step2_sender')"
    >
      <div class="p-6">
        <div class="flex relative">
          <div class="flex justify-center mb-5 w-full">
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x=".5"
                y=".5"
                width="39"
                height="39"
                rx="5.5"
                :fill="appPrimaryColorExtraLight"
                :stroke="appPrimaryColorDeep"
              />
              <path
                d="m15 15 10 10M25 15 15 25"
                :stroke="appPrimaryColorDeep"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <div
            class="absolute right-0 cursor-pointer"
            @click="closeSenderDetailModal"
          >
            <CloseIcon class="text-grey-700 h-6 w-6"/>
          </div>
        </div>
        <VuePerfectScrollbar
          class="overflow-scroll"
          style="max-height: calc(-250px + 100vh)"
        >
          <div class="text-center" v-html="senderLinkedModulesTemplate"></div>
        </VuePerfectScrollbar>
        <div class="flex text-center">
          <div>
            <p class="text-sm font-bold text-deep-dark mt-6 mb-4">
              {{
                $t(
                  'frontend_groovemail_senders_update_verified_sender_warning'
                )
              }}
            </p>
            <text-input
              variant="base"
              v-model="senderUpdateText"
              :placeholder="$t('frontend_groovemail_senders_update_verified_sender_add_yes_placeholder')"
            />
            <div
              class="text-sm text-left text-danger mt-2"
              v-if="
                this.senderUpdateInitiated &&
                this.senderUpdateText.toLowerCase() !== 'yes'
              "
            >
              {{
                $t(
                  'frontend_groovemail_senders_update_verified_sender_add_yes'
                )
              }}
            </div>
          </div>
        </div>
        <div class="flex justify-center mt-6">
          <gd-button
            size="auto"
            @click="updateCurrentSender"
          >
            {{ $t('frontend_update') }}
          </gd-button>
          <gd-button
            class="ml-2"
            variant="primary"
            size="auto"
            @click="closeSenderDetailModal"
          >
            {{ $t('frontend_close') }}</gd-button
          >
        </div>
      </div>
    </gs-named-modal>
  </div>
</template>

<script>
import auth from '@groovepages/gd-lib/auth';
import {
  required,
  minLength,
  maxLength,
  email,
} from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import Notify from '@groovepages/gd-ui-kit/notify';
import ContentInIframe from '@/components/content-in-iframe.vue';
import SenderCreationSteps from './sender-creation-steps.vue';
import { sendersUrl } from '@/utils/urls.js';
import { getData, postData, putData } from '@/utils/apiHelper.js';
import themeConfig from '@/theme/themeConfig.js';
import showAlert from '@/components/sweet-alert-popup.js';

// import icons
import InfoIcon from '@/assets/images/icons/new/info_new.svg';
import CloseIcon from '@/assets/images/icons/new/close_small_new.svg';
import ExitIcon from '@/assets/images/icons/new/exit_arrow_new.svg';

export default {
  name: 'CreateSender',
  layout: 'defaultWithoutVuePerfectScrollbar',
  middleware: 'auth',

  metaInfo() {
    return { title: this.$t('frontend_groovemail_sender_title') };
  },

  data() {
    return {
      sender: {},
      resendSavedSenderId: null,
      isLoading: false,
      senderLinkedModulesTemplate: '',
      senderLinkedModulesCount: 0,
      senderUpdateInitiated: false,
      senderUpdateText: '',
      webhookUrl: '',
      turialLinkForExternalSmtp: '',
      formSubmitted: false,
      currentTab: 'senderInfo',
      model: {
        company_name: '',
        address: '',
        name: '',
        sender_name: '',
        sender_email: '',
        reply_email: '',
        is_default: 0,
        smtp_driver: 0,
      },
      smtp_model: {
        user_name: '',
        api_key: '',
        server: '',
        configuration_set: '',
        webhook_signing_key: '',
      },
      senderCreationSteps: [],
      appPrimaryColorDeep: themeConfig.appPrimaryColorDeep,
      appPrimaryColorExtraLight: themeConfig.appPrimaryColorExtraLight,
    };
  },

  components: {
    InfoIcon,
    VuePerfectScrollbar,
    CloseIcon,
    ExitIcon,
    SenderCreationSteps,
    ContentInIframe
  },

  validations: {
    model: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
      sender_name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
      sender_email: {
        required,
        email,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
      reply_email: {
        required,
        email,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
      company_name: {
        required,
      },
      address: {
        required,
      },
    },
    smtp_model: {
      user_name: {
        required,
      },
      api_key: {
        required,
      },
      server: {
        required,
      },
      configuration_set: {
        required,
      },
      webhook_signing_key: {
        required,
      },
    },
  },

  methods: {
    //  Can mail/Upgraded user/Sandbox mode check
    // getters from store.
    ...mapGetters('mailing', ['getCanMail', 'getCanAccessInternalSmtp']),
    //  Can mail/Upgraded user/Sandbox mode check


    // show loader
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    // hide loader
    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    // go back to the sender settings page on click of back button
    moveBack() {
      // go back to the sender settings page
      this.$router.push({ name: 'Settings', query: { type: 'sender' } });
    },


    // error message for the request field
    async requestErrorAlert(error) {
      // hide the loader
      this.hideLoader();
      
      await showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-cancel { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark mb-3">${error || this.$t('frontend_backend_error') }</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

	  /**
     * Used to create html template for senders linked modules(if any) to be shown in a modal.
     * @param {object} data modules data
     * @return {string} - html content
     */
    sendersLinkedModules(data) {
      let senderDetailTemplate = `<p class="text-lg font-bold text-deep-dark mb-3"> ${this.$t(
        'frontend_groovemail_senders_deletion_swal_heading_text'
      )}</p>
        <p class="text-sm text-deep-dark mb-6"> ${this.$t(
          'frontend_groovemail_senders_deletion_swal_subheading_text'
        )}</p>`;

      if (
        data.campaigns &&
        data.campaigns.length > 0 &&
        !data.campaigns.every((element) => element === null)
      ) {
        senderDetailTemplate += `<p class="text-sm text-deep-dark font-bold mb-4">${this.$t(
          'frontend_groovemail_broadcasts_route_page_title'
        )}: <span class="font-normal">${data.campaigns.join(', ')}</span></p>`;
      }
      if (
        data.forms &&
        data.forms.length > 0 &&
        !data.forms.every((element) => element === null)
      ) {
        senderDetailTemplate += `<p class="text-sm text-deep-dark font-bold mb-4">${this.$t(
          'frontend_groovemail_forms_route_page_title'
        )}: <span class="font-normal">${data.forms.join(', ')}</span></p>`;
      }
      if (
        data.sequences &&
        data.sequences.length > 0 &&
        !data.sequences.every((element) => element === null)
      ) {
        senderDetailTemplate += `<p class="text-sm text-deep-dark font-bold">${this.$t(
          'frontend_groovemail_sequences_route_page_title'
        )}: <span class="font-normal">${data.sequences.join(', ')}</span></p>`;
      }
      return senderDetailTemplate;
    },

    // close sender detail modal
    closeSenderDetailModal() {
      this.$root.$emit('modal-close', 'senders-detail-modal');
    },

    // Triggers updating current sender
    async updateCurrentSender() {
      this.senderUpdateInitiated = true;
      if (this.senderUpdateText.toLowerCase() === 'yes') {
        await this.senderCreateUpdate();
      }
    },

    // Used to notify the user on the success of copying webhook url
    onCopyWebhookUrl() {
      Notify.success(
        this.$t('frontend_groovemail_senders_webhook_url_copy_success')
      );
    },

    // Used to notify the user on the failure of copying webhook url
    onErrorWebhookUrl() {
      Notify.fail(
        this.$t('frontend_groovemail_senders_webhook_url_copy_error')
      );
    },

	  /**
     * Used to open a modal to display video walkthorugh of creating 'Sendgrid' sender.
     * @param {number} driverType SMTP Driver type
     */
    openWalkthroughVideoForExternalSmtp(driverType) {
      if (driverType === 3) {
        this.turialLinkForExternalSmtp =
          'https://www.youtube.com/embed/SbspqKZAtFg';
      }
      this.$root.$emit('modal-open', 'external-smtp-setup-walkthrough-video');
    },

    // Used to open modal to display steps to configure sender with AWS SMTP
    openStepsForAwsSmtp() {
      this.senderCreationSteps = [...this.awsSteps];
      this.$root.$emit('modal-open', 'smtp-setup-walkthrough-steps');
    },

    // Used to open modal to display steps to configure sender with Mailgun SMTP
    openStepsForMailgunSmtp() {
      this.senderCreationSteps = [...this.mailgunSteps];
      this.$root.$emit('modal-open', 'smtp-setup-walkthrough-steps');
    },

    /**
     * Triggered on change of SMTP Driver.
     * @param {object} option SMTP option selected
     */
    smtpDriverUpdated(option) {
      this.getWebhookUrl(option.id);
      this.$v.smtp_model.api_key.$reset();
      this.formSubmitted = false;
    },

	  /**
     * Used to assign webhook URL based on SMTP Driver type.
     * @param {number} driverId driver type
     */
    getWebhookUrl(driverId) {
      const gdMailAPIsUrl = window.config.apiUrl;

      switch (driverId) {
        case 2:
          this.webhookUrl = `${gdMailAPIsUrl}/handle-mailgun-webhook/${this.$store.state.user.id}`;
          break;
        case 3:
          this.webhookUrl = `${gdMailAPIsUrl}/handle-sendgrid-webhook/${this.$store.state.user.id}`;
          break;
        case 4:
          this.webhookUrl = `${gdMailAPIsUrl}/handle-sparkpost-webhook/${this.$store.state.user.id}`;
          break;
        case 5:
          this.webhookUrl = `${gdMailAPIsUrl}/handle-ses-webhook/${this.$store.state.user.id}`;
          break;
        default:
          this.webhookUrl = '';
      }
    },
	
    /**
     * Triggered on change of tabs.
     * @param {string} newTab new tab
     */
    handleTabChange(newTab) {
      this.currentTab = newTab;
    },

    // Triggered on change of sender default type
    changeDefault() {
      this.model.is_default = this.model.is_default !== 1 ? 1 : 0;
    },
	  
    /**
     * Used to open modal to update sender in case of sender linked to modules,
     * otherwise creates/updates sender.
     */
    async createEmailSender() {
      // call api and check if it has linked modules
      if (
        this.senderLinkedModulesCount > 0 &&
        this.$route.name === 'EditSender' &&
        this.sender.is_verified === 1 &&
        this.sender.sender_email !== this.model.sender_email.toLowerCase()
      ) {
        this.senderUpdateInitiated = false;
        this.senderUpdateText = '';
        this.$root.$emit('modal-open', 'senders-detail-modal');
      }
      await this.senderCreateUpdate();
    },

    async createNewSender() {
      this.$v.model.name.$touch();
      this.$v.model.sender_name.$touch();
      this.$v.model.sender_email.$touch();
      this.$v.model.company_name.$touch();
      this.$v.model.address.$touch();
      this.$v.model.reply_email.$touch();

      // check for SMTP driver type
      if (this.model.smtp_driver === 2) {
        this.$v.smtp_model.user_name.$touch();
        this.$v.smtp_model.api_key.$touch();
        this.$v.smtp_model.webhook_signing_key.$touch();
      } else if (this.model.smtp_driver === 3) {
        this.$v.smtp_model.api_key.$touch();
      } else if (this.model.smtp_driver === 4 || this.model.smtp_driver === 6) {
        this.$v.smtp_model.api_key.$touch();
      } else if (this.model.smtp_driver === 5) {
        this.$v.smtp_model.user_name.$touch();
        this.$v.smtp_model.api_key.$touch();
        this.$v.smtp_model.server.$touch();
        this.$v.smtp_model.configuration_set.$touch();
      }
      
      // used to show error message on submit
      this.formSubmitted = true;

      // check for sender info validit
      const userInfoValid = this.$v.model.name.$invalid ||
        this.$v.model.sender_name.$invalid ||
        this.$v.model.sender_email.$invalid ||
        this.$v.model.reply_email.$invalid;

      // check for sender info and business info
      if (userInfoValid || this.checkSmtpSettingsInvalid()) {
        this.currentTab = 'senderInfo';

        // if sender info is invalid, scroll to the top of the page
        if (userInfoValid) {
          // scroll to the top of the page
          this.scrollToTop();
        }

      } else if (this.$v.model.company_name.$invalid || this.$v.model.address.$invalid) {
        this.currentTab = 'businessInfo';

        // scroll to the top of the page if business info is invalid
        this.scrollToTop();
      } else {
        // scroll to the top of the page
        this.scrollToTop();

        // call api to create/update sender
        await this.createEmailSender();
      }
    },

    // scroll to the top of the page
    scrollToTop() {
      document.getElementsByTagName('main')[0].scrollTop = 0;
    },

    // Triggers creation/updation of sender based on SMTP driver type
    async senderCreateUpdate() {
      this.sender.name = this.model.name;
      this.sender.sender_name = this.model.sender_name;
      this.sender.sender_email = this.model.sender_email.toLowerCase();
      this.sender.reply_email = this.model.reply_email.toLowerCase();
      this.sender.company_name = this.model.company_name;
      this.sender.address = this.model.address;
      this.sender.is_default = this.model.is_default;
      this.sender.driver = this.model.smtp_driver;
      if (this.model.smtp_driver === 2) {
        this.sender.user_name = this.smtp_model.user_name;
        this.sender.api_key = this.smtp_model.api_key;
        this.sender.webhook_signing_key = this.smtp_model.webhook_signing_key;
      } else if (this.model.smtp_driver === 3) {
        this.sender.api_key = this.smtp_model.api_key;
      } else if (this.model.smtp_driver === 4 || this.model.smtp_driver === 6) {
        this.sender.api_key = this.smtp_model.api_key;
      } else if (this.model.smtp_driver === 5) {
        this.sender.user_name = this.smtp_model.user_name;
        this.sender.api_key = this.smtp_model.api_key;
        this.sender.server = this.smtp_model.server;
        this.sender.configuration_set = this.smtp_model.configuration_set;
      }

	    let response;
      try {
        // show loader
        this.showLoader();

        // call api to create/update sender
        if (this.$route.name === 'EditSender' || this.resendSavedSenderId) {
          const senderId = this.resendSavedSenderId || this.$route.params.id;
          response = await putData(`${sendersUrl.senders}/${senderId}`, this.sender );
        } else {
          response = await postData(sendersUrl.senders, this.sender);
        }

        // hide loader
        this.hideLoader();
      } catch (error) {
        // stay on the same tab
        this.currentTab = 'senderInfo';

        // show error message
        this.requestErrorAlert(error.response?.data?.error);

        this.hideLoader();
        if (error.response?.data?.sender_id) {
          this.resendSavedSenderId = error.response.data.sender_id;
        }
        // The sender ID is preserved when setup needs a retry.
        return;
      }
	      
      if (this.$route.name === 'NewSender' || this.$route.name === 'EditSender' ) {
        this.$router.push({ name: 'Settings', query: { type: 'sender' } });
      } else {
        this.$emit('emailSenderCreated', response.data);
      }
    },

    // Used to check sender validity based on SMTP Driver type
    checkSmtpSettingsInvalid() {
      const smtpDriver = this.model.smtp_driver;
      const smtpUserNameInValid = this.$v.smtp_model.user_name.$invalid;
      const smtpApiKeyInValid = this.$v.smtp_model.api_key.$invalid;
      const smtpServerInValid = this.$v.smtp_model.server.$invalid;
      const smtpConfigurationSetInValid = this.$v.smtp_model.configuration_set.$invalid;
      const smtpWebhookSigningKeyInValid = this.$v.smtp_model.webhook_signing_key.$invalid;

      // if condation for checking SMTP driver type and its validity
      if(
        // If the SMTP driver is set to "None", return true
        (smtpDriver === 0) ||
        // If the SMTP driver is set to "Sendinblue" and any of the required fields are invalid, return true
        (smtpDriver === 2 && (smtpUserNameInValid || smtpApiKeyInValid || smtpWebhookSigningKeyInValid)) ||
        // If the SMTP driver is set to "Mailgun" and the API key is invalid, return true
        (smtpDriver === 3 && smtpApiKeyInValid) ||
        // If the SMTP driver is set to "Sendgrid" and the API key is invalid, return true
        ((smtpDriver === 4 || smtpDriver === 6) && smtpApiKeyInValid) ||
        // If the SMTP driver is set to "Amazon SES" and any of the required fields are invalid, return true
        (smtpDriver === 5 && (smtpUserNameInValid || smtpApiKeyInValid || smtpServerInValid || smtpConfigurationSetInValid))
      ) {
        return true;
      }
      
      return false;
    },
	  
    // Used to inialize sender creation fields on sender creation/updation
    async initializeSenderData() {
      if (this.$route.params.id && this.$route.name === 'EditSender') {
        
        // show loader
        this.showLoader();

        try {
          const senderData = await getData(`${sendersUrl.senders}/${this.$route.params.id}`, null, true);
          this.sender = senderData?.data?.data;
          this.senderLinkedModulesTemplate = this.sendersLinkedModules(
            senderData?.data?.modules
          );
          this.senderLinkedModulesCount =
            senderData?.data?.modules?.campaigns.length +
            senderData?.data?.modules?.forms.length +
            senderData?.data?.modules?.sequences.length;
          this.model.name = this.sender.name;
          this.model.sender_name = this.sender.sender_name;
          this.model.sender_email = this.sender.sender_email;
          this.model.reply_email = this.sender.reply_email;
          this.model.company_name = this.sender.company_name;
          this.model.address = this.sender.address;
          this.model.is_default = this.sender.is_default;
          this.model.smtp_driver =
            !this.userCanAccessInternalSmtp && this.sender.driver === 1
              ? 0
              : this.sender.driver;
          this.getWebhookUrl(this.model.smtp_driver);
          if (this.model.smtp_driver === 2) {
            this.smtp_model.user_name = this.sender.user_name;
            this.smtp_model.api_key = this.sender.api_key;
            this.smtp_model.webhook_signing_key =
              this.sender.webhook_signing_key;
          } else if (this.model.smtp_driver === 3) {
            this.smtp_model.api_key = this.sender.api_key;
          } else if (this.model.smtp_driver === 4 || this.model.smtp_driver === 6) {
            this.smtp_model.api_key = this.sender.api_key;
          } else if (this.model.smtp_driver === 5) {
            this.smtp_model.user_name = this.sender.user_name;
            this.smtp_model.api_key = this.sender.api_key;
            this.smtp_model.server = this.sender.server;
            this.smtp_model.configuration_set = this.sender.configuration_set;
          }
          // hide loader
          this.hideLoader();
        } catch (error) {
          // hide loader
          this.requestErrorAlert(error.response?.data?.error);
        }
      } else if (!this.$route.params.id) {
        this.sender = {};
      }
    },
  },

  // Fetches sender data using API call and assigns the same.
  async created() {
    await this.initializeSenderData();
  },

  computed: {
    // Used to return brandData value from store.
    supBrandName() {
      return this.$store.getters.supBrand.title;
    },
	  /**
     * Returns array which contains sender tab data.
     * @return {array} - returns array for sender's tabs.
     */
    tabs() {
      return [
        {
          title: `<span class="text-sm leads-field font-bold mr-3"></i>&nbsp;${this.$t(
            'frontend_groovemail_sender_sender_info_tab'
          )}<span>`,
          value: 'senderInfo',
        },
        {
          title: `<span class="text-sm leads-field font-bold mr-3"></i>&nbsp;${this.$t(
            'frontend_groovemail_sender_business_info_tab'
          )}<span>`,
          value: 'businessInfo',
        },
      ];
    },

    /**
     * Returns array which contains AWS sender creation steps.
     * @return {array} - array of steps.
     */
    awsSteps() {
      return [
        {
          stepLabel: `<p><b>${this.$t(
            'frontend_groovemail_step'
          )} 1:</b> ${this.$t(
            'frontend_groovemail_aws_step_1'
          )} (<a target="_blank" href="https://aws.amazon.com/console/" class="cursor-pointer text-primary underline">click here to visit page</a>).</p>`,
        },
        {
          stepLabel: `<p><b>${this.$t(
            'frontend_groovemail_step'
          )} 2:</b> ${this.$t('frontend_groovemail_aws_step_2')}</p>`,
        },
        {
          stepLabel: `<p><b>${this.$t(
            'frontend_groovemail_step'
          )} 3:</b> ${this.$t('frontend_groovemail_aws_step_3')}</p>`,
        },
        {
          stepLabel: `<p><b>${this.$t(
            'frontend_groovemail_step'
          )} 4:</b> ${this.$t('frontend_groovemail_aws_step_4')}</p>`,
          subSteps: [
            {
              subStepLabel: `<span>${this.$t(
                'frontend_groovemail_aws_step_4_substep_1'
              )}</span>`,
            },
            {
              subStepLabel: `<span>${this.$t(
                'frontend_groovemail_aws_step_4_substep_2'
              )}</span>`,
            },
            {
              subStepLabel: `<span>${this.$t(
                'frontend_groovemail_aws_step_4_substep_3'
              )}</span>`,
            },
            {
              subStepLabel: `<span>${this.$t(
                'frontend_groovemail_aws_step_4_substep_4'
              )}</span>`,
            },
          ],
        },
        {
          stepLabel: `<p><b>${this.$t(
            'frontend_groovemail_step'
          )} 5:</b> ${this.$t('frontend_groovemail_aws_step_5')}</p>`,
        },
        {
          stepLabel: `<p><b>${this.$t(
            'frontend_groovemail_step'
          )} 6:</b> ${this.$t('frontend_groovemail_aws_step_6')}</p>`,
          subSteps: [
            {
              subStepLabel: `<span>${this.$t(
                'frontend_groovemail_aws_step_6_substep_1'
              )}</span>`,
            },
            {
              subStepLabel: `<span>${this.$t(
                'frontend_groovemail_aws_step_6_substep_2'
              )}</span>`,
            },
          ],
        },
        {
          stepLabel: `<p><b>${this.$t(
            'frontend_groovemail_step'
          )} 7:</b> ${this.$t('frontend_groovemail_aws_step_7')}</p>`,
        },
      ];
    },

	  /**
     * Returns array which contains Mailgun sender creation steps.
     * @return {array} - array of steps.
     */
    mailgunSteps() {
      return [
        {
          stepLabel: `<p><b>${this.$t(
            'frontend_groovemail_step'
          )} 1:</b> ${this.$t(
            'frontend_groovemail_mailgun_step_1_a'
          )} <a target="_blank" href="https://www.mailgun.com/" class="cursor-pointer text-primary underline">${this.$t(
            'frontend_groovemail_mailgun_step_1_b'
          )}</a> ${this.$t('frontend_groovemail_mailgun_step_1_c')}.</p>`,
        },
        {
          stepLabel: `<p><b>${this.$t('frontend_groovemail_step')} 2:</b> ${this.$t('frontend_groovemail_mailgun_step_2')}</p>`,
        },
        {
          stepLabel: `<p><b>${this.$t('frontend_groovemail_step')} 3:</b> ${this.$t('frontend_groovemail_mailgun_step_3')}</p>`,
        },
        {
          stepLabel: `<p><b>${this.$t('frontend_groovemail_step')} 4:</b> ${this.$t('frontend_groovemail_mailgun_step_4')}</p>`,
        },
        {
          stepLabel: `<p><b>${this.$t('frontend_groovemail_step')} 5:</b> ${this.$t('frontend_groovemail_mailgun_step_5')}</p>`,
          subSteps: [
            {
              subStepLabel: `<span>${this.$t('frontend_groovemail_mailgun_step_5_substep_1')}</span>`,
            },
            {
              subStepLabel: `<span>${this.$t('frontend_groovemail_mailgun_step_5_substep_2')}</span>`,
            },
            {
              subStepLabel: `<span>${this.$t('frontend_groovemail_mailgun_step_5_substep_3')}</span>`,
            },
          ],
        },
        {
          stepLabel: `<p><b>${this.$t('frontend_groovemail_step')} 6:</b> ${this.$t('frontend_groovemail_mailgun_step_6')}</p>`,
        },
      ];
    },

	  /**
     * Returns the possibility of user to use internal SMTP.
     * It is a admin controlled check
     * @return {number} - 1 - can use, 0 - can't use
     */
    // admin section controlled check
    userCanAccessInternalSmtp() {
      return this.getCanAccessInternalSmtp();
    },

	  /**
     * Returns the possibility of user to use internal SMTP.
     * It is a plan limit based check
     * @return {number} - 1 - can use, 0 - can't use
     */
    // new plans based limit check
    canUseGrooveMailSmtp() {
      return auth.fetchFeatureValue('groovemail_can_use_groovemail_smtp') === 1;
    },

    //  Can mail/Upgraded user/Sandbox mode check
	  /**
     * Returns the possibility of user to send emails.
     * It is a admin controlled check
     * @return {number} - 1 - can use, 0 - can't use
     */
    userCanMail() {
      return this.getCanMail();
    },
    //  Can mail/Upgraded user/Sandbox mode check

    /**
     * Returns main tab class for tabs.
     * @return {string} tab class
     */
    mainTabClass() {
      if (this.currentTab === 'senderInfo') {
        return 'pb-2 gs-default-tabs-item';
      }
      return 'pb-2 gs-default-tabs-item';
    },

	  /**
     * Returns an array which contains SMTP Driver types.
     * @return {array} - array of drivers.
     */
    smtp_options() {
      const smtpOptions = [];
      const smtpDriverTitle = `${this.supBrandName} SMTP`;

      if (this.userCanAccessInternalSmtp) {
        smtpOptions.push({
          id: 1,
          title: smtpDriverTitle,
          coming_soon: !this.canUseGrooveMailSmtp,
          disabled_message: !this.canUseGrooveMailSmtp
            ? this.$t('frontend_groovemail_sender_settings_restricted_smtp_for_free_account')
            : '',
        });
      }
      smtpOptions.push({
        id: 2,
        title: 'MailGun',
      });
      smtpOptions.push({
        id: 3,
        title: 'SendGrid',
      });
      smtpOptions.push({
        id: 4,
        title: 'SparkPost',
      });
      smtpOptions.push({
        id: 5,
        title: 'Amazon-SES',
      });
      smtpOptions.push({ id: 6, title: 'Resend' });
      return smtpOptions;
    },
  },
};
</script>

<style scoped>
.gm-sender-settings {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-sender-settings__card {
  max-width: 960px;
  margin: 0 auto;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gm-sender-settings__header {
  color: var(--gs-foreground);
}

.gm-sender-settings__body {
  min-width: 0;
}

@media (max-width: 640px) {
  .gm-sender-settings {
    padding: 16px;
  }

  .gm-sender-settings__card {
    border-radius: var(--gs-radius-md);
  }

  .gm-sender-settings__body {
    padding: 20px 18px 0;
  }
}
</style>
